import { useState, useRef, useEffect } from 'react'
import { ChevronDownIcon, SwatchIcon } from '@heroicons/react/24/outline'

const PRESETS = [
  {
    name: 'Default',
    appearance: {
      primaryColor: '#6366f1',
      backgroundColor: '#ffffff',
      textColor: '#1f2937',
      headerColor: '#6366f1',
      userMessageColor: '#6366f1',
      assistantMessageColor: '#f3f4f6',
    },
  },
  {
    name: 'Dark Mode',
    appearance: {
      primaryColor: '#8b5cf6',
      backgroundColor: '#1e1b4b',
      textColor: '#e2e8f0',
      headerColor: '#312e81',
      userMessageColor: '#7c3aed',
      assistantMessageColor: '#1e1b4b',
    },
  },
  {
    name: 'Minimal',
    appearance: {
      primaryColor: '#6b7280',
      backgroundColor: '#ffffff',
      textColor: '#374151',
      headerColor: '#f9fafb',
      userMessageColor: '#e5e7eb',
      assistantMessageColor: '#f3f4f6',
    },
  },
  {
    name: 'Colorful',
    appearance: {
      primaryColor: '#ec4899',
      backgroundColor: '#fdf2f8',
      textColor: '#831843',
      headerColor: '#ec4899',
      userMessageColor: '#f472b6',
      assistantMessageColor: '#fce7f3',
    },
  },
]

export default function PresetSelector({ onSelect }) {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelect = (preset) => {
    onSelect(preset.appearance)
    setIsOpen(false)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <label className="block text-sm font-medium text-slate-700 mb-1.5">
        Theme Preset
      </label>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-2.5 border border-slate-200 rounded-xl text-sm bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
      >
        <div className="flex items-center space-x-2">
          <SwatchIcon className="h-4 w-4 text-slate-400" />
          <span className="text-slate-700">Select a preset theme...</span>
        </div>
        <ChevronDownIcon
          className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden">
          {PRESETS.map((preset) => (
            <button
              key={preset.name}
              onClick={() => handleSelect(preset)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-slate-50 transition-colors text-left"
            >
              <span className="text-sm font-medium text-slate-700">
                {preset.name}
              </span>
              <div className="flex items-center space-x-1">
                <div
                  className="w-4 h-4 rounded-full border border-slate-200"
                  style={{ backgroundColor: preset.appearance.primaryColor }}
                  title="Primary"
                />
                <div
                  className="w-4 h-4 rounded-full border border-slate-200"
                  style={{ backgroundColor: preset.appearance.backgroundColor }}
                  title="Background"
                />
                <div
                  className="w-4 h-4 rounded-full border border-slate-200"
                  style={{ backgroundColor: preset.appearance.headerColor }}
                  title="Header"
                />
                <div
                  className="w-4 h-4 rounded-full border border-slate-200"
                  style={{ backgroundColor: preset.appearance.userMessageColor }}
                  title="User Message"
                />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
